import React from 'react';
import { FIG_INK, FIG_TEXT, FIG_RED } from '../figureStyle';

// ─── New-paradigm arrivals (tiny at-a-glance sketch) ──────────────────────────
// For the astralcodexten "new paradigms" link tooltip: major AI ideas plotted
// on a timeline, with the gaps between arrivals visibly shrinking as the field
// grows. Solid baseline through "now" (mid-2026), then dashed into the future
// ending in a "?" — when does the next one land? Same visual language as the
// simple-takeoff sketches: no axis lines, minimal ink, red data marks.

const VX = -20;
const VW = 312;
const VH = 90;
const PLOT_L = 8;
const PLOT_R = 292;
const BASE_Y = 48;

const YEAR_MIN = 1950;
const YEAR_MAX = 2034;
const NOW = 2026.5;

const LABEL_FS = 8;

// `dx` nudges a label (and its leader line) sideways so the tightly packed
// recent milestones don't overlap; the dot stays at the true year.
const MILESTONES: { year: number; name: string; side: 'above' | 'below'; dx: number }[] = [
  { year: 1957, name: 'Neural nets', side: 'above', dx: 0 },
  { year: 1986, name: 'Backprop', side: 'below', dx: 0 },
  { year: 2012, name: 'Deep learning', side: 'above', dx: -16 },
  { year: 2017, name: 'Transformers', side: 'below', dx: -24 },
  { year: 2022, name: 'Chatbots', side: 'above', dx: 14 },
  { year: 2024, name: 'Reasoning', side: 'below', dx: 12 },
];

const x = (year: number) =>
  PLOT_L + ((year - YEAR_MIN) / (YEAR_MAX - YEAR_MIN)) * (PLOT_R - PLOT_L);

export default function SimpleParadigmTimeline() {
  return (
    <div className="mx-auto mb-4">
      <svg viewBox={`${VX} 0 ${VW} ${VH}`} className="w-full h-auto font-mono">
        <line
          x1={PLOT_L} y1={BASE_Y} x2={x(NOW)} y2={BASE_Y}
          stroke={FIG_INK} strokeWidth={1.25}
        />
        <line
          x1={x(NOW)} y1={BASE_Y} x2={PLOT_R} y2={BASE_Y}
          stroke={FIG_INK} strokeWidth={1.25} strokeDasharray="4 3"
        />
        <text x={285} y={BASE_Y - 5} textAnchor="middle" fontSize={LABEL_FS + 1} fill={FIG_TEXT} opacity={0.7}>
          ?
        </text>

        {MILESTONES.map(({ year, name, side, dx }) => {
          const dotX = x(year);
          const labelX = dotX + dx;
          // Year sits nearest the line on both sides, the name beyond it.
          const nameY = side === 'above' ? 16 : 80;
          const yearY = side === 'above' ? 25 : 71;
          const leader: [number, number, number, number] = side === 'above'
            ? [labelX, 29, dotX, BASE_Y - 5]
            : [dotX, BASE_Y + 5, labelX, 63];
          return (
            <g key={year}>
              <line
                x1={leader[0]} y1={leader[1]} x2={leader[2]} y2={leader[3]}
                stroke={FIG_INK} strokeWidth={0.5} opacity={0.4}
              />
              <text x={labelX} y={nameY} textAnchor="middle" fontSize={LABEL_FS} fill={FIG_TEXT}>
                {name}
              </text>
              <text x={labelX} y={yearY} textAnchor="middle" fontSize={LABEL_FS} fill={FIG_TEXT} opacity={0.55}>
                {year}
              </text>
              <circle cx={dotX} cy={BASE_Y} r={2.5} fill={FIG_RED} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
